import React, { useMemo } from 'react';
import {
  Checkbox, Popconfirm, Table, Tag,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';

import {
  IPreviewRow,
  PREVIEW_PAGE_SIZE,
  ROW_LEVEL_KEY,
} from '../../../../utils/asset-type-import';
import { ReactComponent as ThrashCan } from '../../../../assets/icons/thrashCan.svg';
import { IPreviewStepProps } from './i-import-wizard';
import PreviewCell from './preview-cell';

const TAG_BASE: React.CSSProperties = {
  margin: 0,
  borderRadius: 4,
  fontSize: 12,
  lineHeight: '20px',
};

const NEW_TAG: React.CSSProperties = {
  ...TAG_BASE,
  background: '#f6ffed',
  border: '1px solid #b7eb8f',
  color: '#52c41a',
};

const ERROR_TAG: React.CSSProperties = {
  ...TAG_BASE,
  background: '#fdecec',
  border: '1px solid #f4b8b8',
  color: '#e13333',
};

const GREY_TAG: React.CSSProperties = {
  ...TAG_BASE,
  background: '#fafafa',
  border: '1px solid #d9d9d9',
  color: '#8c909b',
};

const REASON_BASE: React.CSSProperties = { fontSize: 12, marginTop: 2 };

const firstValue = (map: Record<string, string>): string => {
  const keys = Object.keys(map);
  return keys.length > 0 ? map[keys[0]] : '';
};

const reasonOf = (row: IPreviewRow): string => (
  row.errors[ROW_LEVEL_KEY]
  || firstValue(row.errors)
  || row.warnings[ROW_LEVEL_KEY]
  || firstValue(row.warnings)
);

const renderReason = (reason: string, color: string): JSX.Element | null => (
  reason ? <div style={{ ...REASON_BASE, color }}>{reason}</div> : null
);

const renderStatus = (row: IPreviewRow): JSX.Element => {
  const reason = reasonOf(row);

  if (row.status === 'invalid') {
    return (
      <div>
        <Tag style={ERROR_TAG}>Invalid</Tag>
        {renderReason(reason, '#E13333')}
      </div>
    );
  }

  if (row.status === 'duplicate') {
    return (
      <div>
        <Tag style={GREY_TAG}>Duplicate — will be skipped</Tag>
        {renderReason(reason, '#8C909B')}
      </div>
    );
  }

  return (
    <div>
      <Tag style={NEW_TAG}>New</Tag>
      {renderReason(reason, '#8C909B')}
    </div>
  );
};

const renderSkipped = (value: string): JSX.Element => (
  <span style={{ color: '#B5B8BF' }}>{value}</span>
);

const rowClassName = (row: IPreviewRow): string => {
  if (row.status === 'invalid') return 'import-row--invalid';
  if (row.status === 'duplicate') return 'import-row--duplicate';
  return '';
};

const PreviewStep = ({
  rows,
  plan,
  summary,
  truncated,
  onlyProblems,
  disabled,
  onToggleOnlyProblems,
  onCellChange,
  onPropChange,
  onDropRow,
}: IPreviewStepProps): JSX.Element => {
  const visibleRows = useMemo(
    () => (onlyProblems ? rows.filter((row) => row.status !== 'new') : rows),
    [rows, onlyProblems],
  );

  const columns = useMemo<ColumnsType<IPreviewRow>>(() => {
    const propertyColumns: ColumnsType<IPreviewRow> = plan.propertyColumns.map(
      (column, index) => ({
        title: column.name,
        key: `prop-${index}`,
        width: 180,
        render: (_: unknown, row: IPreviewRow) => (
          row.status === 'duplicate' ? renderSkipped(row.props[index] ?? '') : (
            <PreviewCell
              value={row.props[index] ?? ''}
              error={row.errors[`props.${index}`]}
              disabled={disabled}
              onChange={(value) => onPropChange(row.key, index, value)}
            />
          )
        ),
      }),
    );

    return [
      {
        title: 'Line',
        dataIndex: 'line',
        key: 'line',
        fixed: 'left',
        width: 64,
        render: (line: number) => <span style={{ color: '#8C909B' }}>{line}</span>,
      },
      {
        title: 'Status',
        key: 'status',
        fixed: 'left',
        width: 240,
        render: (_: unknown, row: IPreviewRow) => renderStatus(row),
      },
      {
        title: 'Code',
        key: 'code',
        width: 200,
        render: (_: unknown, row: IPreviewRow) => (
          row.status === 'duplicate' ? renderSkipped(row.code) : (
            <PreviewCell
              value={row.code}
              error={row.errors.code}
              disabled={disabled}
              onChange={(value) => onCellChange(row.key, 'code', value)}
            />
          )
        ),
      },
      {
        title: 'Description',
        key: 'description',
        width: 260,
        render: (_: unknown, row: IPreviewRow) => (
          row.status === 'duplicate' ? renderSkipped(row.description) : (
            <PreviewCell
              value={row.description}
              error={row.errors.description}
              disabled={disabled}
              onChange={(value) => onCellChange(row.key, 'description', value)}
            />
          )
        ),
      },
      ...propertyColumns,
      {
        title: '',
        key: 'actions',
        width: 56,
        align: 'center',
        render: (_: unknown, row: IPreviewRow) => (
          <Popconfirm
            title="Remove this row from the import?"
            okText="Remove"
            cancelText="Cancel"
            disabled={disabled}
            onConfirm={() => onDropRow(row.key)}
          >
            <button
              type="button"
              aria-label="Remove row"
              disabled={disabled}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                lineHeight: 0,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <ThrashCan />
            </button>
          </Popconfirm>
        ),
      },
    ];
  }, [plan.propertyColumns, disabled, onCellChange, onPropChange, onDropRow]);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
        }}
      >
        {summary.newCount > 0 ? (
          <Tag style={NEW_TAG}>{`${summary.newCount} new`}</Tag>
        ) : null}
        {summary.duplicateCount > 0 ? (
          <Tag style={GREY_TAG}>
            {`${summary.duplicateCount} duplicates — will be skipped`}
          </Tag>
        ) : null}
        {summary.invalidCount > 0 ? (
          <Tag style={ERROR_TAG}>{`${summary.invalidCount} invalid`}</Tag>
        ) : null}
        <span style={{ color: '#8C909B', fontSize: 13 }}>
          {`${summary.total} rows total`}
        </span>
      </div>

      {truncated ? (
        <div
          style={{
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 8,
            color: '#d48806',
            fontSize: 13,
            padding: '8px 12px',
            marginBottom: 12,
          }}
        >
          {`Only the first ${summary.total} rows of this file were read — the rest of the file was `}
          not imported. Split the file and run the import again to bring in the remaining rows.
        </div>
      ) : null}

      {plan.propertyRenames.length > 0 ? (
        <div
          style={{
            background: '#fafafa',
            border: '1px solid #e8e8e8',
            borderRadius: 8,
            color: '#535559',
            fontSize: 13,
            padding: '8px 12px',
            marginBottom: 12,
          }}
        >
          {'Matched to properties this tenant already uses: '}
          {plan.propertyRenames.map((rename) => `${rename.from} \u2192 ${rename.to}`).join(', ')}
          {'. Otherwise the import would create a second property differing only in case. '}
          Change it on the Map columns step if that is wrong.
        </div>
      ) : null}

      <div style={{ marginBottom: 12 }}>
        <Checkbox checked={onlyProblems} onChange={onToggleOnlyProblems}>
          Show only rows needing attention
        </Checkbox>
      </div>

      <div style={{ border: '1px solid #f1f2f5', borderRadius: 11, overflow: 'hidden' }}>
        <Table
          size="small"
          rowKey="key"
          columns={columns}
          dataSource={visibleRows}
          rowClassName={rowClassName}
          pagination={{ pageSize: PREVIEW_PAGE_SIZE, showSizeChanger: false }}
          scroll={{ x: 'max-content', y: 360 }}
        />
      </div>
    </div>
  );
};

PreviewStep.displayName = 'PreviewStep';

export default PreviewStep;
